// Calendar-day arithmetic on ISO day strings (YYYY-MM-DD). All business rules use
// "day strings in the advisor's local calendar" so that results never shift with
// time zones or DST. Time-of-day is carried separately (HH:MM).

export type Day = string;

export function toDay(d: Date): Day {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const x = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${x}`;
}
export const today = (): Day => toDay(new Date());
export const nowIso = (): string => new Date().toISOString();

function parts(d: Day): [number, number, number] {
  const [y, m, x] = d.split('-').map(Number);
  return [y!, m!, x!];
}
function utc(d: Day): number { const [y, m, x] = parts(d); return Date.UTC(y, m - 1, x); }
function fromUtc(ms: number): Day { return new Date(ms).toISOString().slice(0, 10); }

export function addDays(d: Day, n: number): Day { return fromUtc(utc(d) + n * 86400000); }
export function addMonths(d: Day, n: number): Day {
  const [y, m, x] = parts(d);
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12), nm = total % 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return fromUtc(Date.UTC(ny, nm, Math.min(x, last)));
}
/** whole days from a to b (b - a) */
export function diffDays(a: Day, b: Day): number { return Math.round((utc(b) - utc(a)) / 86400000); }
export const isDay = (v: unknown): v is Day => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** next occurrence (>= from) of an annual date given as YYYY-MM-DD or MM-DD; Feb 29 → Feb 28 in common years */
export function nextAnnual(date: string, from: Day): Day {
  const md = date.length === 5 ? date : date.slice(5);
  const [m, x] = md.split('-').map(Number);
  const [fy] = parts(from);
  for (const y of [fy, fy + 1]) {
    const last = new Date(Date.UTC(y, m!, 0)).getUTCDate();
    const cand = fromUtc(Date.UTC(y, m! - 1, Math.min(x!, last)));
    if (cand >= from) return cand;
  }
  return from;
}

export function ageOn(birth: Day, on: Day): number {
  const [by, bm, bd] = parts(birth);
  const [y, m, d] = parts(on);
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}

export function weekday(d: Day): number { return new Date(utc(d)).getUTCDay(); }
export function startOfWeek(d: Day, firstDay = 1): Day { return addDays(d, -((weekday(d) - firstDay + 7) % 7)); }

/** Robust parse of legacy date inputs: ISO, DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, Date strings */
export function normalizeDay(v: unknown): Day | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : toDay(v);
  const s = String(v).trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return valid(+m[1]!, +m[2]!, +m[3]!);
  m = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/.exec(s);
  if (m) return valid(+m[3]!, +m[2]!, +m[1]!);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}
function valid(y: number, m: number, d: number): Day | null {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1800 || y > 2300) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1) return null;
  return fromUtc(dt.getTime());
}
export function normalizeTime(v: unknown): string | null {
  if (!v) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v).trim());
  if (!m) return null;
  const h = +m[1]!, mi = +m[2]!;
  if (h > 23 || mi > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
}
export function normalizeDateTime(v: unknown, fallback: string): string {
  if (!v) return fallback;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) { const d = new Date(s); if (!isNaN(d.getTime())) return d.toISOString(); }
  const day = normalizeDay(s);
  return day ? `${day}T12:00:00.000Z` : fallback;
}
